import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { environment } from '@environments/environment';
import {ApiResponse, User} from '@app/_models';
import { Task } from '@app/_components/task';
import {retry, catchError, map} from 'rxjs/operators';
import {BehaviorSubject, Observable, Subscription, throwError} from 'rxjs';
import {AlertService, AuthenticationService} from '@app/_services';
import { SharedService } from './../_shared/shared.service';

@Injectable({ providedIn: 'root' })
export class TaskService
{
  private taskSummarySubject: BehaviorSubject<any>;
  public currentTaskSummary: Observable<any>;
  currentUser: User;
  currentUserSubscription: Subscription;
  statuses = [
	  { value: 0, name: 'Complete' },
	  { value: 1, name: 'In Progress' },
	  { value: 2, name: 'Not started' },
	  { value: 3, name: 'On Hold' },
	  { value: 4, name: 'Cancelled' },
	  { value: 5, name: 'Ready to Review' },
	  { value: 6, name: 'Ready for Prod' },
	  { value: 7, name: 'Reopened'}
	];
  priorities = [
    { value: 1, name: 'Critical' },
    { value: 2, name: 'Immediate' },
    { value: 3, name: 'Important' },
    { value: 4, name: 'Standard' },
    { value: 5, name: 'Low' }
  ];
  private static readonly DATE_RANGE_PARAM_MAP: Record<string, [string, string]> = {
    dueDate: ['dueDateStart', 'dueDateEnd'],
    completedDate: ['completedDateStart', 'completedDateEnd'],
    createdDate: ['createdDateStart', 'createdDateEnd'],
    followupDate: ['followupDateStart', 'followupDateEnd']
  };

	constructor
		(
			private http: HttpClient,
			private alertService: AlertService,
      private authenticationService: AuthenticationService,
			private sharedService: SharedService
		) {

    this.currentUserSubscription = this.authenticationService.currentUser.subscribe(user => {
      this.currentUser = user;
      if (this.currentUser && !localStorage.getItem('taskSummaryCount')){
        this.getTaskSummary(this.currentUser.userId).subscribe(dataCount =>
        {
          this.taskSummarySubject = new BehaviorSubject<any>(JSON.parse(localStorage.getItem('taskSummaryCount')));
          this.currentTaskSummary = this.taskSummarySubject.asObservable();
        });
      }
      else{
        this.taskSummarySubject = new BehaviorSubject<any>(JSON.parse(localStorage.getItem('taskSummaryCount')));
        this.currentTaskSummary = this.taskSummarySubject.asObservable();
      }
      this.taskSummarySubject = new BehaviorSubject<any>(JSON.parse(localStorage.getItem('taskSummaryCount')));
      this.currentTaskSummary = this.taskSummarySubject.asObservable();
    });
  }

	urlEndpoint = 'tasks';

	add(task: Task): Observable<ApiResponse>
	{
		console.log('posting... ' + JSON.stringify(task));
		return this.http.post<ApiResponse>(`${ environment.apiUrl }/${ this.urlEndpoint }`, task);
	}
  addWithParent(task: Task, parentTaskId: number): Observable<ApiResponse>
  {
    console.log('posting... ' + JSON.stringify(task));
    return this.http.post<ApiResponse>(`${ environment.apiUrl }/${ this.urlEndpoint }/${ parentTaskId }`, task);
  }

	update(task: Task): Observable<ApiResponse>
	{
		return this.http.patch<ApiResponse>(`${ environment.apiUrl }/${ this.urlEndpoint }`, task);
	}

	delete(taskId: number): Observable<ApiResponse>
	{
		return this.http.delete<ApiResponse>(`${ environment.apiUrl }/${ this.urlEndpoint }/${ taskId }`);
	}

	findAll(): Observable<ApiResponse>
	{
		return this.http.get<ApiResponse>(`${ environment.apiUrl }/${ this.urlEndpoint }`).pipe(retry({ count: 2, delay: 1500 }));
	}

	findByFilter(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any): Observable<ApiResponse>
	{

		let params = this.sharedService.prepareParamsFromPrimeNgDataTable(page, size, sortBy, sortOrder, multiSortMeta, globalFilter, filters);
		return this.http.get<ApiResponse>(`${ environment.apiUrl }/${ this.urlEndpoint }`, { params: params }).pipe(retry({ count: 2, delay: 1500 }));
	}

	findByFilterSummary(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any): Observable<ApiResponse>
	{

		let params = this.sharedService.prepareParamsFromPrimeNgDataTable(page, size, sortBy, sortOrder, multiSortMeta, globalFilter, filters);
		return this.http.get<ApiResponse>(`${ environment.apiUrl }/${ this.urlEndpoint }/summary`, { params: params }).pipe(retry({ count: 2, delay: 1500 }));
	}
 
  findByFilterSummaryWithDateRange(
    page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any,
    dateRange: any,
    dateField?: 'dueDate' | 'completedDate' | 'createdDate' | 'followupDate'
  ): Observable<ApiResponse> {
    let params = this.sharedService.prepareParamsFromPrimeNgDataTable(page, size, sortBy, sortOrder, multiSortMeta, globalFilter, filters);
    let appender = '';
    if (dateRange && dateRange.length > 1) {
      const [startKey, endKey] = (dateField && TaskService.DATE_RANGE_PARAM_MAP[dateField])
        ? TaskService.DATE_RANGE_PARAM_MAP[dateField]
        : ['startDate', 'endDate'];
      appender = '?' + startKey + '=' + encodeURIComponent(dateRange[0]) + '&' + endKey + '=' + encodeURIComponent(dateRange[1]);
    }
    return this.http.get<ApiResponse>(`${ environment.apiUrl }/${ this.urlEndpoint }/summary/${appender}`, { params: params }).pipe(retry({ count: 2, delay: 1500 }));
  }
  findPublicTasksByFilterSummary(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any): Observable<ApiResponse>
  {
    let params = this.sharedService.prepareParamsFromPrimeNgDataTable(page, size, sortBy, sortOrder, multiSortMeta, globalFilter, filters);
    return this.http.get<ApiResponse>(`${ environment.apiUrl }/${ this.urlEndpoint }/publictasksummary`, { params: params }).pipe(retry({ count: 2, delay: 1500 }));
  }
  findByTaskListSummary(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any, userId: number, type: string): Observable<ApiResponse>
  {

    let params = this.sharedService.prepareParamsFromPrimeNgDataTable(page, size, sortBy, sortOrder, multiSortMeta, globalFilter, filters);
    return this.http.get<ApiResponse>(`${ environment.apiUrl }/${ this.urlEndpoint }/tasksummarylist/${userId}/${type}`, { params: params }).pipe(retry({ count: 2, delay: 1500 }));
  }

	findById(id: number): Observable<ApiResponse>
	{
		return this.http.get<ApiResponse>(`${ environment.apiUrl }/${ this.urlEndpoint }/${ id }`).pipe(retry({ count: 2, delay: 1500 }));
	}
  tagSummaryList(tagType): Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/tags/summary-list?tagType=${tagType}`).pipe(retry({ count: 2, delay: 1500 }));
  }
  getTaskSummary(userId: number): Observable<ApiResponse>
  {
    return this.http.get<ApiResponse>(`${ environment.apiUrl }/${ this.urlEndpoint }/getsummary/${ userId }`).pipe(retry({ count: 2, delay: 1500 }))
      .pipe(map(apiResponse => {
        console.log('login: apiResponse:' + JSON.stringify(apiResponse.data));
        if (apiResponse.data.task) {
          //caching summary count to save the network calls
          localStorage.setItem('taskSummaryCount', JSON.stringify(apiResponse.data.task));
          this.taskSummarySubject.next(apiResponse.data.task);
          this.taskSummarySubject = new BehaviorSubject<any>(JSON.parse(localStorage.getItem('taskSummaryCount')));
          this.currentTaskSummary = this.taskSummarySubject.asObservable();
        }
        return apiResponse;
      }));
  }

	audit(id: number): Observable<ApiResponse>
	{
		return this.http.get<ApiResponse>(`${ environment.apiUrl }/${ this.urlEndpoint }/${ id }/audit`).pipe(retry({ count: 2, delay: 1500 }));
	}


	updateShortTermRentalId(data): Observable<ApiResponse>
	{
		return this.http.patch<ApiResponse>(`${ environment.apiUrl }/${ this.urlEndpoint }/update-short-term-rental`, data);
	}

	updateAssignedToUserId(data): Observable<ApiResponse>
	{
		return this.http.patch<ApiResponse>(`${ environment.apiUrl }/${ this.urlEndpoint }/update-assign-to-user`, data);
	}
  updateRequestedByUserId(data): Observable<ApiResponse>
  {
    return this.http.patch<ApiResponse>(`${ environment.apiUrl }/${ this.urlEndpoint }/update-requested-by-user`, data);
  }

	updateAssignedToTeamId(data): Observable<ApiResponse>
	{
		return this.http.patch<ApiResponse>(`${ environment.apiUrl }/${ this.urlEndpoint }/update-assign-to-team`, data);
	}


	// addTaskTag(data): Observable<ApiResponse>
	// {
	// 	// addTaskTag allows ONE tag of same tagType for each task (ie project #5)
	// 	console.log('posting... ' + JSON.stringify(data));
	// 	return this.http.post<ApiResponse>(`${ environment.apiUrl }/task-tags/save-one`, data);
	// }

	addTaskTags(data): Observable<ApiResponse>
	{
		// addTaskTags allows multiple tags of same tagType for each task (ie tags #4)
		console.log('posting... ' + JSON.stringify(data));
		return this.http.post<ApiResponse>(`${ environment.apiUrl }/task-tags`, data);
	}

	deleteTaskTag(data): Observable<ApiResponse>
	{
		console.log('posting... ' + JSON.stringify(data));
		return this.http.delete<ApiResponse>(`${ environment.apiUrl }/task-tags/${ data.taskId }/${ data.tagId }`);
	}

	getFolderNav(taskTag: any): Observable<ApiResponse> {
		// addTaskTag allows ONE tag of same tagType for each task (ie project #5)
		console.log('posting... ' + JSON.stringify(taskTag));
		return this.http.post<ApiResponse>(`${environment.apiUrl}/tags/folder-nav`,taskTag);
	}

	updateTaskReservation(data): Observable<ApiResponse>
	{
		// addReservationTag allows ONE tag of same tagType for each reservation (ie project #5)
		console.log('posting... ' + JSON.stringify(data));
		return this.http.patch<ApiResponse>(`${ environment.apiUrl }/${ this.urlEndpoint }/update-reservation`, data);
	}

	deleteTaskReservation(data): Observable<ApiResponse>
	{
		console.log('posting... ' + JSON.stringify(data));
		return this.http.delete<ApiResponse>(`${ environment.apiUrl }/${ this.urlEndpoint }/delete-reservation/${ data.taskId }`);
	}

	addTaskUser(data): Observable<ApiResponse>
	{
		console.log('posting... ' + JSON.stringify(data));
		return this.http.post<ApiResponse>(`${ environment.apiUrl }/task-users`, data);
	}

	addTaskUserContact(data): Observable<ApiResponse>
	{
		// addTaskTags allows multiple tags of same tagType for each task (ie users #4)
		console.log('posting... ' + JSON.stringify(data));
		return this.http.post<ApiResponse>(`${ environment.apiUrl }/task-users/contacts`, data);
	}

	updateTaskUser(data): Observable<ApiResponse>
	{
		// addTaskTags allows multiple tags of same tagType for each task (ie users #4)
		console.log('posting... ' + JSON.stringify(data));
		return this.http.patch<ApiResponse>(`${ environment.apiUrl }/task-users`, data);
	}

	updateTaskUserContact(data): Observable<ApiResponse>
	{
		// addTaskTags allows multiple tags of same tagType for each task (ie users #4)
		console.log('posting... ' + JSON.stringify(data));
		return this.http.patch<ApiResponse>(`${ environment.apiUrl }/task-users/contacts`, data);
	}

	deleteTaskUser(data): Observable<ApiResponse>
	{
		console.log('posting... ' + JSON.stringify(data));
		return this.http.delete<ApiResponse>(`${ environment.apiUrl }/task-users/${ data.taskId }/${ data.userId }`);
	}

	findTaskUsers(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any, taskId: any): Observable<ApiResponse>
	{
		let params = this.sharedService.prepareParamsFromPrimeNgDataTable(page, size, sortBy, sortOrder, multiSortMeta, globalFilter, filters);

		// add the source
		if (taskId) { params = params.append('taskId', taskId); }

		return this.http.get<ApiResponse>(`${ environment.apiUrl }/task-users/find-by-task`, { params: params }).pipe(retry({ count: 2, delay: 1500 }));
	}
	findTaskUser(taskId: any, userId: any): Observable<ApiResponse>
	{
		return this.http.get<ApiResponse>(`${ environment.apiUrl }/task-users/${ taskId }/${ userId }`).pipe(retry({ count: 2, delay: 1500 }));
	}

	documentUploadConfirm(data): Observable<ApiResponse>
	{
		// addTaskTags allows multiple tags of same tagType for each task (ie users #4)
		console.log('posting... ' + JSON.stringify(data));
		return this.http.post<ApiResponse>(`${ environment.apiUrl }/task-documents/upload-confirm`, data);
	}
	documentDownloadPresign(data): Observable<ApiResponse>
	{
		// addTaskTags allows multiple tags of same tagType for each task (ie users #4)
		console.log('posting... ' + JSON.stringify(data));
		return this.http.post<ApiResponse>(`${ environment.apiUrl }/task-documents/download-presign`, data);
	}
	documentRemove(data): Observable<ApiResponse>
	{
		// addTaskTags allows multiple tags of same tagType for each task (ie users #4)
		console.log('posting... ' + JSON.stringify(data));
		return this.http.post<ApiResponse>(`${ environment.apiUrl }/task-documents/remove`, data);
	}



	prepareParamsFromPrimeNgDataTable(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any)
	{
		try
		{
			console.log(
				'  |  page:' + page
				+ '  |  size:' + size
				+ '  |  sortBy:' + sortBy
				+ '  |  sortOrder:' + sortOrder
				+ '  |  multiSortMeta:' + JSON.stringify(multiSortMeta)
				+ '  |  globalFilter:' + globalFilter
				+ '  |  filters:' + JSON.stringify(filters)
			);

			let params = new HttpParams();

			if (!isNaN(page)) { params = params.append('page', page); }
			if (!isNaN(size)) { params = params.append('size', size); }
			if (sortBy) { params = params.append('sort', sortBy + ',' + (sortOrder === 1 ? 'asc' : 'desc')); }
			if (multiSortMeta)
			{
				// if sortMode="multiple" in primeng table then multiSortMeta is used for ALL sorting (even just one column.)
				// Otherwise, single sorting is used above.
				// event.multiSortMeta:[{"field":"lastName","order":1},{"field":"firstName","order":1}]
				for (let i = 0; i < multiSortMeta.length; i++)
				{
					let json = JSON.parse(JSON.stringify(multiSortMeta[i]));
					params = params.append('sort', json.field + ',' + (json.order === 1 ? 'asc' : 'desc'));
				}
			}
			// globalFilter works, but did not implemnt in Spring but not hard to write custom findAll and specify certain columns to look.
			if (globalFilter) { params = params.append('filter', globalFilter); }

			// multiselect or input or whatever... these work slick.
			if (filters)
			{
				// key:status val:{"value":["A"],"matchMode":"in"}
				for (let i = 0, keys = Object.keys(filters), ii = keys.length; i < ii; i++)
				{
					console.log('key:' + keys[i] + ' val:' + JSON.stringify(filters[keys[i]]));
					params = params.append(keys[i], filters[keys[i]].value);
				}
			}
			return params;
		} catch (error)
		{
			console.log('prepareParamsFromPrimeNgDataTable()' + error);
		}
	}
	findTasksWith(queryString: string): Observable<ApiResponse>
	{
		return this.http.get<ApiResponse>(`${ environment.apiUrl }/tasks/find-with?query=${ queryString }`).pipe(retry({ count: 2, delay: 1500 }));
	}
	addParent(taskId: number, parentTaskId: number): Observable<ApiResponse>
	{
		return this.http.get<ApiResponse>(`${ environment.apiUrl }/tasks/addparent/${taskId}/${parentTaskId}`).pipe(retry({ count: 2, delay: 1500 }));
	}
	deleteParent(taskId: number): Observable<ApiResponse>
	{
		return this.http.get<ApiResponse>(`${ environment.apiUrl }/tasks/deleteparent/${taskId}`).pipe(retry({ count: 2, delay: 1500 }));
	}
  sendStatusUpdateReq(task: Task): Observable<ApiResponse>
  {
    console.log('posting... ' + JSON.stringify(task));
    return this.http.post<ApiResponse>(`${ environment.apiUrl }/${ this.urlEndpoint }/send-status-update-req`, task);
  }

	getTemplates(page: number = 0, size: number = 50): Observable<ApiResponse>
	{
		return this.http.get<ApiResponse>(`${ environment.apiUrl }/${ this.urlEndpoint }/summary?isTemplate=true&sort=name,asc&page=${page}&size=${size}`).pipe(retry({ count: 2, delay: 1500 }));
	}

	instantiateTemplate(templateTaskId: number, overrides: any): Observable<ApiResponse>
	{
		return this.http.post<ApiResponse>(`${ environment.apiUrl }/${ this.urlEndpoint }/${templateTaskId}/instantiate`, overrides || {});
	}
}
